import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { learningAnalyticsAPI, InfrastructureCorrelationData } from '../../services/learningAnalyticsApi';

const InfrastructureCorrelationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<InfrastructureCorrelationData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getInfrastructureCorrelation(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load infrastructure correlation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district, level]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, factors, schools } = data;

  const districts = schools
    ? Array.from(new Set(schools.map((s: any) => s.district_name))).sort()
    : [];

  // Prepare chart data: each factor gets two bars
  const chartData = factors.map((f) => ({
    factor: f.factor,
    with_attendance: f.with_attendance,
    without_attendance: f.without_attendance,
    attendance_difference: f.attendance_difference,
  }));

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022, 2021].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {districts.map((d: string) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Schools
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {summary.total_schools.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Power %
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#f57c00' }}>
                {summary.pct_with_power.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Water Access %
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#1976d2' }}>
                {summary.pct_with_water.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Library %
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#388e3c' }}>
                {summary.pct_with_library.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grouped Bar Chart: Infrastructure Factors vs Attendance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Infrastructure Factor Impact on Attendance
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Compares average attendance rates for schools with and without each infrastructure factor.
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="factor" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} label={{ value: 'Attendance %', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value.toFixed(1)}%`,
                  name === 'with_attendance' ? 'With Factor' : 'Without Factor',
                ]}
              />
              <Legend formatter={(value: string) =>
                value === 'with_attendance' ? 'With Factor' : 'Without Factor'
              } />
              <Bar dataKey="with_attendance" fill="#388e3c" name="with_attendance">
                <LabelList
                  dataKey="attendance_difference"
                  position="top"
                  formatter={(v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                  style={{ fontSize: 11, fontWeight: 'bold', fill: '#333' }}
                />
              </Bar>
              <Bar dataKey="without_attendance" fill="#f57c00" name="without_attendance" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Factors Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Infrastructure Factors Detail</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Factor</TableCell>
                  <TableCell align="right">With Count</TableCell>
                  <TableCell align="right">Without Count</TableCell>
                  <TableCell align="right">With Att %</TableCell>
                  <TableCell align="right">Without Att %</TableCell>
                  <TableCell align="right">Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {factors.map((f) => (
                  <TableRow key={f.factor} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{f.factor}</Typography>
                    </TableCell>
                    <TableCell align="right">{f.with_count.toLocaleString()}</TableCell>
                    <TableCell align="right">{f.without_count.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: '#388e3c' }}>
                      {f.with_attendance.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#f57c00' }}>
                      {f.without_attendance.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right"
                      sx={{ color: f.attendance_difference >= 0 ? '#388e3c' : '#d32f2f', fontWeight: 'bold' }}>
                      {f.attendance_difference >= 0 ? '+' : ''}{f.attendance_difference.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Schools Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Schools Overview</Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="center">Power</TableCell>
                  <TableCell align="center">Water</TableCell>
                  <TableCell align="center">Library</TableCell>
                  <TableCell align="right">Rooms</TableCell>
                  <TableCell align="right">Enrollment</TableCell>
                  <TableCell align="right">Attendance %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.map((s: any) => (
                  <TableRow key={s.school_reg_number} hover>
                    <TableCell>{s.school_name}</TableCell>
                    <TableCell>{s.district_name}</TableCell>
                    <TableCell align="center">{s.has_power ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="center">{s.has_water ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="center">{s.has_library ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">{s.total_rooms ?? '-'}</TableCell>
                    <TableCell align="right">{s.enrollment?.toLocaleString() ?? '-'}</TableCell>
                    <TableCell align="right">
                      {s.student_attendance_rate != null ? `${s.student_attendance_rate.toFixed(1)}%` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InfrastructureCorrelationTab;
